import {
  OreButton,
  OreHeading,
  OreInput,
  OreModal,
  OreModalBox,
  OreModalBoxBody,
  OreModalBoxFooter,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { handleErrors } from '@/Shared/ui/form'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { useTpv } from '@/Store/ui/context'
import { TpvForm } from '@/Store/ui/hooks'
import { storesTranslation } from '@/Store/ui/storesTranslation'
import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'

export type AddTpvModalActions = {
  open: () => void
}

export const AddTpvModal = forwardRef<AddTpvModalActions>((_, ref): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false)
  const { t } = useTranslation()

  const { addNewTpv, form } = useTpv()

  const { register, formState } = form
  const { errors } = handleErrors(formState)

  useImperativeHandle(ref, () => ({
    open: async (): Promise<void> => {
      form.reset()
      setOpen(true)
    },
  }))

  const saveAndClose = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event?.preventDefault()

    form.handleSubmit(async (data: TpvForm) => {
      const result = await addNewTpv(data)
      if (result) {
        setOpen(false)
      }
    })()
  }

  return (
    <OreModal open={open} handleOnClose={() => setOpen(false)}>
      <OreModalBox size="medium" handleOnClose={() => setOpen(false)}>
        <OreModalBoxBody>
          <OreHeading align="left" size="headline-md">
            {t(storesTranslation.tpvs.modal.title)}
          </OreHeading>
          <OreStack space="large">
            <OreText align="left">{t(storesTranslation.tpvs.modal.text)}</OreText>
            <OreInput
              {...register('terminalMid')}
              {...errors('terminalMid')}
              id="name"
              label={t(storesTranslation.tpvs.modal.terminalId.label)}
              placeholder={t(AutoEnrollmentTranslation.storeTpvsData.tpvs.placeHolder)}
              required
              maxLength={255}
            />
          </OreStack>
        </OreModalBoxBody>
        <OreModalBoxFooter>
          <OreButton onClick={() => setOpen(false)} size="small" category="secondary">
            {t(formTranslation.common.buttons.cancel)}
          </OreButton>
          <OreButton
            data-testid="confirm"
            onClick={saveAndClose}
            size="small"
            category="primary"
            disabled={!formState.isValid}
          >
            {t(formTranslation.common.buttons.add)}
          </OreButton>
        </OreModalBoxFooter>
      </OreModalBox>
    </OreModal>
  )
})
